import { fetchWithAuth } from '@/lib/auth/fetchWithAuth';
import { getAuthBaseUrl } from '@/lib/auth/getAuthBaseUrl';
import { formatBackendDateTimeFromDate } from './roomDates';
import { joinErrorMessage } from './joinErrors';

export type RoomParticipant = {
  id: number;
  username: string;
  full_name: string;
  avatar: string;
  is_owner: boolean;
  is_moderator: boolean;
  kicked: boolean;
};

export type RoomParticipantRef = {
  id?: number;
  email?: string;
  username?: string;
};

export type RoomHistoryItem = {
  id: number;
  address: string;
  name: string;
  description?: string;
  color?: string;
  starts_at: string;
  ends_at: string;
  encrypt_key?: string;
  unauthorized_access?: boolean;
  invite_only?: boolean;
  participants?: RoomParticipant[];
  is_creator: boolean;
  is_participant: boolean;
};

export type CreateRoomRequest = {
  name?: string;
  description?: string;
  color?: string;
  starts_at?: string;
  ends_at?: string;
  unauthorized_access?: boolean;
  encryption?: boolean;
  invite_only?: boolean;
  planned?: boolean;
  participants?: RoomParticipantRef[];
};

export type CreateRoomResponse = {
  id: number;
  address: string;
  name: string;
  description?: string;
  color?: string;
  starts_at: string;
  ends_at: string;
  unauthorized_access?: boolean;
  invite_only?: boolean;
  participants?: RoomParticipant[];
  encrypt_key?: string;
};

export type UpdateRoomRequest = {
  id: number;
  name?: string;
  description?: string;
  color?: string;
  starts_at?: string;
  ends_at?: string;
  unauthorized_access?: boolean;
  invite_only?: boolean;
  participants?: RoomParticipantRef[];
};

export type JoinRoomRequest = {
  address: string;
  encrypt_key?: string;
  participant_name: string;
  participant_identity?: string;
  participant_metadata?: string;
  participant_attributes?: Record<string, string>;
};

export type JoinRoomResponse = {
  server_url?: string;
  participant_token?: string;
  is_owner: boolean;
  is_moderator: boolean;
  kicked: boolean;
  not_invited?: boolean;
};

export type RoomRecording = {
  file_id: number;
  prefix: string;
  created_at: string;
  expired: boolean;
};

export type RecordingFileLink = {
  expiration: string;
  link: string;
};

export class RoomJoinError extends Error {
  readonly status?: number;
  readonly needsAuth: boolean;

  constructor(message: string, options?: { status?: number; needsAuth?: boolean }) {
    super(message);
    this.name = 'RoomJoinError';
    this.status = options?.status;
    this.needsAuth = options?.needsAuth ?? false;
  }
}

type HistoryResponse = { rooms: RoomHistoryItem[] };
type RecordingsResponse = { recordings: RoomRecording[] };

async function readErrorMessage(resp: Response): Promise<string> {
  try {
    const data = (await resp.json()) as { error?: string; unknown_participants?: string[] };
    if (typeof data?.error === 'string' && data.error) {
      if (data.unknown_participants?.length) {
        return `${data.error}: ${data.unknown_participants.join(', ')}`;
      }
      return data.error;
    }
  } catch {}
  const text = await resp.text().catch(() => '');
  return text || `${resp.status} ${resp.statusText}`.trim();
}

function authFetch(input: string, init?: RequestInit) {
  return fetchWithAuth(input, {
    ...init,
    redirectOnUnauthorized: true,
  });
}

export async function createRoom(payload: CreateRoomRequest): Promise<CreateRoomResponse> {
  const resp = await authFetch(`${getAuthBaseUrl()}/auth/v1/room`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    if (resp.status === 401) {
      throw new Error('Сессия истекла. Войдите снова.');
    }
    throw new Error(await readErrorMessage(resp));
  }
  return (await resp.json()) as CreateRoomResponse;
}

export async function getRoomHistory(params: {
  from: Date;
  to: Date;
}): Promise<RoomHistoryItem[]> {
  const from = encodeURIComponent(formatBackendDateTimeFromDate(params.from));
  const to = encodeURIComponent(formatBackendDateTimeFromDate(params.to));
  const resp = await authFetch(
    `${getAuthBaseUrl()}/auth/v1/room/history?from=${from}&to=${to}`,
    { method: 'GET' },
  );
  if (!resp.ok) {
    if (resp.status === 401) {
      throw new Error('Сессия истекла. Войдите снова.');
    }
    throw new Error(await readErrorMessage(resp));
  }
  const data = (await resp.json()) as HistoryResponse;
  return data.rooms ?? [];
}

export async function cancelRoom(id: number): Promise<void> {
  const resp = await authFetch(`${getAuthBaseUrl()}/auth/v1/room`, {
    method: 'DELETE',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ id }),
  });
  if (!resp.ok) {
    if (resp.status === 401) {
      throw new Error('Сессия истекла. Войдите снова.');
    }
    throw new Error(await readErrorMessage(resp));
  }
}

export async function updateRoom(payload: UpdateRoomRequest): Promise<CreateRoomResponse> {
  const resp = await authFetch(`${getAuthBaseUrl()}/auth/v1/room`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    if (resp.status === 401) {
      throw new Error('Сессия истекла. Войдите снова.');
    }
    throw new Error(await readErrorMessage(resp));
  }

  return (await resp.json()) as CreateRoomResponse;
}

export async function joinRoom(payload: JoinRoomRequest): Promise<JoinRoomResponse> {
  const resp = await fetchWithAuth(`${getAuthBaseUrl()}/auth/v1/room/join`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
    redirectOnUnauthorized: false,
  });

  if (!resp.ok) {
    const serverError = await readErrorMessage(resp);
    throw new RoomJoinError(joinErrorMessage(resp.status, serverError), {
      status: resp.status,
      needsAuth: resp.status === 401,
    });
  }

  const data = (await resp.json()) as JoinRoomResponse;
  if (data.kicked) {
    throw new RoomJoinError('Вы были удалены из этой конференции');
  }
  if (data.not_invited) {
    throw new RoomJoinError('Вы не приглашены в эту конференцию');
  }
  if (!data.server_url || !data.participant_token) {
    throw new RoomJoinError('Не удалось получить доступ к комнате');
  }

  return data;
}

export async function getRoomRecordings(address: string): Promise<RoomRecording[]> {
  const resp = await authFetch(
    `${getAuthBaseUrl()}/auth/v1/room/recordings?address=${encodeURIComponent(address)}`,
    { method: 'GET' },
  );
  if (!resp.ok) {
    if (resp.status === 401) {
      throw new Error('Сессия истекла. Войдите снова.');
    }
    throw new Error(await readErrorMessage(resp));
  }
  const data = (await resp.json()) as RecordingsResponse;
  return data.recordings ?? [];
}

export async function getRecordingFileLink(fileId: number): Promise<RecordingFileLink> {
  const resp = await authFetch(
    `${getAuthBaseUrl()}/auth/v1/record/file?id=${encodeURIComponent(String(fileId))}`,
    { method: 'GET' },
  );
  if (!resp.ok) {
    if (resp.status === 401) {
      throw new Error('Сессия истекла. Войдите снова.');
    }
    throw new Error(await readErrorMessage(resp));
  }
  return (await resp.json()) as RecordingFileLink;
}
